import { useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useAdminBulkLoanActionMutation,
  useGetAdminStaffQuery,
} from '@/apis/extended/admin/adminWorkspaceApi';
import { useAdminListLoanApplicationsQuery } from '@/apis/extended/loanApplicationApi';
import {
  AttestationChip,
  daysAgoLabel,
  DocumentsSummary,
  humanizeKey,
  sectionProgress,
} from '@/components/Admin/AdminWorkspace/loanHealth';
import { LoanStatusQuickSelect } from '@/components/Admin/AdminWorkspace/LoanStatusQuickSelect';
import { LoanReviewModal } from '@/components/Admin/LoanReview/LoanReviewModal';
import { SortableTableCell } from '@/components/Common/SortableTableCell';
import { TastePageHeader } from '@/components/Common/TastePageHeader';
import { TasteSectionCard } from '@/components/Common/TasteSectionCard';
import { useTableSort } from '@/hooks/useTableSort';
import type {
  LoanApplication,
  LoanBorrower,
} from '@/types/LoanApplicationType';

const TABLE_COL_COUNT = 12;

/**
 * A cell clipped to one line with "…" (full text on hover). The clip sits on
 * an inner box: browsers don't reliably apply max-width to table cells.
 */
function ClipCell({
  width,
  title,
  bold,
  children,
}: {
  width: number;
  title?: string;
  bold?: boolean;
  children: ReactNode;
}) {
  return (
    <TableCell title={title}>
      <Box
        sx={{
          maxWidth: width,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: bold ? 500 : undefined,
        }}
      >
        {children}
      </Box>
    </TableCell>
  );
}

function primaryBorrower(r: LoanApplication): LoanBorrower | undefined {
  return r.borrowers?.find((b) => b.role === 'primary') ?? r.borrowers?.[0];
}

function applicantName(r: LoanApplication): string {
  const b = primaryBorrower(r);
  const fromBorrower = [b?.firstName, b?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return (
    fromBorrower ||
    r.applicantUser?.name?.trim() ||
    r.applicantUser?.email ||
    '—'
  );
}

function applicantEmail(r: LoanApplication): string {
  const b = primaryBorrower(r);
  return b?.email?.trim() || r.applicantUser?.email?.trim() || '—';
}

type View = 'all' | 'stuck' | 'resign' | 'documents' | 'unassigned';

const VIEWS: {
  key: View;
  label: string;
  test: (r: LoanApplication) => boolean;
}[] = [
  { key: 'all', label: 'All files', test: () => true },
  {
    key: 'stuck',
    label: 'Stuck',
    test: (r) => (r.adminInsights?.stuckReasons.length ?? 0) > 0,
  },
  {
    key: 'resign',
    label: 'Needs re-signing',
    test: (r) => r.adminInsights?.attestation === 'needs_resign',
  },
  {
    key: 'documents',
    label: 'Missing documents',
    test: (r) => (r.adminInsights?.documents.missing.length ?? 0) > 0,
  },
  {
    key: 'unassigned',
    label: 'No loan officer',
    test: (r) => !r.assignedToAdminId,
  },
];

const AdminLoanPipelinePage = () => {
  const theme = useTheme();
  // Twelve columns don't fit a phone; show a card per file instead.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [status, setStatus] = useState('');
  const [productType, setProductType] = useState('');
  const [lifecycle, setLifecycle] = useState('');
  const [officer, setOfficer] = useState('');
  const [view, setView] = useState<View>('all');
  const [reviewing, setReviewing] = useState<LoanApplication | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOfficer, setBulkOfficer] = useState('');
  const [bulkProcessor, setBulkProcessor] = useState('');

  const { data: rows = [], isLoading } = useAdminListLoanApplicationsQuery({
    ...(status ? { status } : {}),
    ...(productType ? { productType } : {}),
  });
  const { data: staff = [] } = useGetAdminStaffQuery();
  const [bulk, { isLoading: bulkBusy }] = useAdminBulkLoanActionMutation();

  const staffName = useMemo(() => {
    const m: Record<string, string> = {};
    staff.forEach((s) => (m[s._id] = s.name));
    return m;
  }, [staff]);
  const officerLabel = (id?: string | null) =>
    id ? staffName[id] || 'Former admin' : 'Unassigned';

  const progress = useMemo(() => {
    const m = new Map<string, ReturnType<typeof sectionProgress>>();
    rows.forEach((r) => m.set(r._id, sectionProgress(r)));
    return m;
  }, [rows]);

  const lifecycles = useMemo(
    () =>
      [
        ...new Set(rows.map((r) => r.adminInsights?.lifecycle).filter(Boolean)),
      ] as string[],
    [rows]
  );

  const filtered = useMemo(() => {
    const viewTest = VIEWS.find((v) => v.key === view)!.test;
    return rows.filter(
      (r) =>
        viewTest(r) &&
        (!lifecycle || r.adminInsights?.lifecycle === lifecycle) &&
        (!officer ||
          (officer === 'none'
            ? !r.assignedToAdminId
            : r.assignedToAdminId === officer))
    );
  }, [rows, view, lifecycle, officer]);

  const viewCounts = useMemo(() => {
    const m: Record<string, number> = {};
    VIEWS.forEach((v) => (m[v.key] = rows.filter(v.test).length));
    return m;
  }, [rows]);

  const sort = useTableSort<LoanApplication>(filtered, {
    name: (r) => applicantName(r).toLowerCase(),
    property: (r) => r.property?.subjectPropertyAddress || null,
    lifecycle: (r) => r.adminInsights?.lifecycle,
    sections: (r) => {
      const p = progress.get(r._id);
      return p && p.total ? p.done / p.total : null;
    },
    attestation: (r) => r.adminInsights?.attestation,
    documents: (r) => r.adminInsights?.documents.missing.length ?? null,
    activity: (r) => r.adminInsights?.daysSinceActivity ?? null,
    officer: (r) =>
      r.assignedToAdminId ? officerLabel(r.assignedToAdminId) : null,
    status: (r) => r.status,
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allVisibleSelected =
    filtered.length > 0 && filtered.every((r) => selected.has(r._id));

  const runBulk = async (
    action: 'assign' | 'remind' | 'request_documents',
    extra: {
      assignedToAdminId?: string | null;
      processorAdminId?: string | null;
    } = {}
  ) => {
    const ids = [...selected];
    try {
      const res = await bulk({ ids, action, ...extra }).unwrap();
      const ok = res.results.filter((r) =>
        ['assigned', 'sent'].includes(r.status)
      ).length;
      const skipped = res.results.length - ok;
      toast.success(
        `${action === 'assign' ? 'Assigned' : 'Emailed'} ${ok} file${
          ok === 1 ? '' : 's'
        }${skipped ? ` · ${skipped} skipped` : ''}`
      );
      setSelected(new Set());
    } catch (e: any) {
      toast.error(e?.data?.error || 'Bulk action failed');
    }
  };

  const sectionsCell = (r: LoanApplication) => {
    const p = progress.get(r._id);
    if (!p) return '—';
    return (
      <Tooltip
        title={
          p.incomplete.length
            ? `To do: ${p.incomplete.join(', ')}`
            : 'All sections done'
        }
      >
        <span>
          {p.done}/{p.total}
        </span>
      </Tooltip>
    );
  };

  return (
    <Box>
      <TastePageHeader
        title="Loan Files"
        subtitle="Every purchase and refinance application, from the first draft to closing. Use Stuck to find files that need a nudge."
      />

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2, overflowX: 'auto', pb: 0.5 }}
      >
        {VIEWS.map((v) => (
          <Chip
            key={v.key}
            label={`${v.label} (${viewCounts[v.key] ?? 0})`}
            color={
              view === v.key
                ? v.key === 'stuck'
                  ? 'warning'
                  : 'primary'
                : 'default'
            }
            variant={view === v.key ? 'filled' : 'outlined'}
            onClick={() => setView(v.key)}
            sx={{ flexShrink: 0 }}
          />
        ))}
      </Stack>

      <TasteSectionCard
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          label="Status"
          select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: { sm: 160 } }}
          fullWidth={isMobile}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="submitted">Submitted</MenuItem>
          <MenuItem value="in_review">In review</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="declined">Declined</MenuItem>
          <MenuItem value="withdrawn">Withdrawn</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Product"
          select
          value={productType}
          onChange={(e) => setProductType(e.target.value)}
          sx={{ minWidth: { sm: 140 } }}
          fullWidth={isMobile}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="dscr">DSCR</MenuItem>
          <MenuItem value="conventional">Conventional</MenuItem>
          <MenuItem value="fha">FHA</MenuItem>
          <MenuItem value="va">VA</MenuItem>
          <MenuItem value="usda">USDA</MenuItem>
          <MenuItem value="bridge">Bridge</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Lifecycle"
          select
          value={lifecycle}
          onChange={(e) => setLifecycle(e.target.value)}
          sx={{ minWidth: { sm: 170 } }}
          fullWidth={isMobile}
        >
          <MenuItem value="">All</MenuItem>
          {lifecycles.map((l) => (
            <MenuItem key={l} value={l}>
              {humanizeKey(l)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Loan officer"
          select
          value={officer}
          onChange={(e) => setOfficer(e.target.value)}
          sx={{ minWidth: { sm: 180 } }}
          fullWidth={isMobile}
        >
          <MenuItem value="">Anyone</MenuItem>
          <MenuItem value="none">Unassigned</MenuItem>
          {staff.map((s) => (
            <MenuItem key={s._id} value={s._id}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
      </TasteSectionCard>

      {selected.size > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            position: 'sticky',
            top: 8,
            zIndex: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {selected.size} selected
          </Typography>
          <TextField
            select
            size="small"
            label="Loan officer"
            value={bulkOfficer}
            onChange={(e) => setBulkOfficer(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="none">Unassigned</MenuItem>
            {staff.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Processor"
            value={bulkProcessor}
            onChange={(e) => setBulkProcessor(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="none">Unassigned</MenuItem>
            {staff.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            size="small"
            disabled={bulkBusy || (!bulkOfficer && !bulkProcessor)}
            onClick={() =>
              void runBulk('assign', {
                ...(bulkOfficer
                  ? {
                      assignedToAdminId:
                        bulkOfficer === 'none' ? null : bulkOfficer,
                    }
                  : {}),
                ...(bulkProcessor
                  ? {
                      processorAdminId:
                        bulkProcessor === 'none' ? null : bulkProcessor,
                    }
                  : {}),
              })
            }
          >
            Assign
          </Button>
          <Tooltip title="Emails each borrower the list of documents still missing on their file">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={bulkBusy}
                onClick={() => void runBulk('request_documents')}
              >
                Request documents
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Re-sign if answers changed, upload if documents are missing, otherwise finish the application">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={bulkBusy}
                onClick={() => void runBulk('remind')}
              >
                Send reminder
              </Button>
            </span>
          </Tooltip>
          <Button size="small" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </Paper>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {isLoading ? (
            <TasteSectionCard sx={{ p: 2 }}>Loading…</TasteSectionCard>
          ) : sort.sortedRows.length === 0 ? (
            <TasteSectionCard sx={{ p: 2 }}>No files match.</TasteSectionCard>
          ) : (
            sort.sortedRows.map((r) => {
              const ins = r.adminInsights;
              return (
                <TasteSectionCard
                  key={r._id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setReviewing(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setReviewing(r);
                  }}
                  sx={{ p: 2, cursor: 'pointer', minWidth: 0 }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      gap={0.5}
                      sx={{ minWidth: 0 }}
                    >
                      <Checkbox
                        size="small"
                        checked={selected.has(r._id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggle(r._id)}
                        sx={{ p: 0.25 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                          {applicantName(r)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {r.property?.subjectPropertyAddress ||
                            'No address yet'}
                        </Typography>
                      </Box>
                    </Stack>
                    <AttestationChip state={ins?.attestation} />
                  </Stack>
                  {ins?.stuckReasons.length ? (
                    <Stack
                      direction="row"
                      gap={0.5}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1 }}
                    >
                      {ins.stuckReasons.map((s) => (
                        <Chip key={s} size="small" color="warning" label={s} />
                      ))}
                    </Stack>
                  ) : null}
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    columnGap={2}
                    rowGap={0.5}
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {humanizeKey(ins?.lifecycle)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sections {sectionsCell(r)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <DocumentsSummary insights={ins} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {daysAgoLabel(ins?.daysSinceActivity)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {officerLabel(r.assignedToAdminId)}
                    </Typography>
                  </Stack>
                </TasteSectionCard>
              );
            })
          )}
        </Stack>
      ) : (
        <TasteSectionCard sx={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Table
            size="small"
            sx={{
              minWidth: 1280,
              '& .MuiTableCell-head': { whiteSpace: 'nowrap' },
              '& .MuiTableCell-body': { verticalAlign: 'middle' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={selected.size > 0 && !allVisibleSelected}
                    onChange={() =>
                      setSelected(
                        allVisibleSelected
                          ? new Set()
                          : new Set(filtered.map((r) => r._id))
                      )
                    }
                  />
                </TableCell>
                <SortableTableCell sortKey="name" sort={sort}>
                  Borrower
                </SortableTableCell>
                <SortableTableCell sortKey="property" sort={sort}>
                  Property
                </SortableTableCell>
                <TableCell>Loan</TableCell>
                <SortableTableCell sortKey="lifecycle" sort={sort}>
                  Lifecycle
                </SortableTableCell>
                <SortableTableCell sortKey="sections" sort={sort}>
                  Sections
                </SortableTableCell>
                <SortableTableCell sortKey="attestation" sort={sort}>
                  Signature
                </SortableTableCell>
                <SortableTableCell sortKey="documents" sort={sort}>
                  Documents
                </SortableTableCell>
                <SortableTableCell sortKey="activity" sort={sort}>
                  Last activity
                </SortableTableCell>
                <SortableTableCell sortKey="officer" sort={sort}>
                  Loan officer
                </SortableTableCell>
                <SortableTableCell sortKey="status" sort={sort}>
                  Status
                </SortableTableCell>
                <TableCell>Needs attention</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COL_COUNT}>Loading…</TableCell>
                </TableRow>
              ) : sort.sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COL_COUNT}>
                    No files match.
                  </TableCell>
                </TableRow>
              ) : (
                sort.sortedRows.map((r) => {
                  const ins = r.adminInsights;
                  return (
                    <TableRow
                      key={r._id}
                      hover
                      selected={selected.has(r._id)}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setReviewing(r)}
                    >
                      <TableCell
                        padding="checkbox"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          size="small"
                          checked={selected.has(r._id)}
                          onChange={() => toggle(r._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 190 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            <Link
                              to={`/admin/user/${r.applicantUserId}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {applicantName(r)}
                            </Link>
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                          >
                            {applicantEmail(r)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <ClipCell
                        width={200}
                        title={r.property?.subjectPropertyAddress}
                      >
                        {r.property?.subjectPropertyAddress || 'No address yet'}
                      </ClipCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {r.flowProfile === 'refinance'
                          ? 'Refinance'
                          : humanizeKey(r.flowProfile)}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {r.productType?.toUpperCase()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {humanizeKey(ins?.lifecycle)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {sectionsCell(r)}
                      </TableCell>
                      <TableCell>
                        <AttestationChip state={ins?.attestation} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <DocumentsSummary insights={ins} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {daysAgoLabel(ins?.daysSinceActivity)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {officerLabel(r.assignedToAdminId)}
                        {r.processorAdminId ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Proc. {officerLabel(r.processorAdminId)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <LoanStatusQuickSelect app={r} />
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          gap={0.5}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ maxWidth: 260 }}
                        >
                          {ins?.stuckReasons.map((s) => (
                            <Chip
                              key={s}
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={s}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TasteSectionCard>
      )}
      <LoanReviewModal
        // Prefer the live row so status edits show; fall back to the snapshot
        // if a filter drops the file from the list mid-review.
        application={
          reviewing
            ? rows.find((r) => r._id === reviewing._id) ?? reviewing
            : null
        }
        onClose={() => setReviewing(null)}
      />
    </Box>
  );
};

export { AdminLoanPipelinePage };
